import React, { useEffect, useMemo } from "react";
import {
  makeStyles,
  shorthands,
  tokens,
  Text,
  Button,
  Divider,
  mergeClasses,
  Tooltip,
} from "@fluentui/react-components";
import {
  AddRegular,
  CheckmarkRegular,
  CheckmarkCircleFilled,
  LockClosedRegular,
} from "@fluentui/react-icons";
import { useFormContext } from "react-hook-form";
import { AdditionalModule, OrderFormData } from "../../../interfaces/OrderFormData";
import { Grid } from "../../common/grid/Grid";
import { GridItem } from "../../common/grid/GridItem";
import { t } from "../../../i18n/i18n";
import { GET_ADDITIONAL_MODULES, GET_BASE_MODULES, GET_PROCESSES } from "../../../graphql/queries";
import { useQuery } from "@apollo/client";
import { BaseModule, Process, Plan, Query } from "../../../graphql/schema";
import { formatPrice, mapTitleToSubscriptionLevel } from "../../../helper/orderHelper";
import { SubscriptionLevel } from "../../../interfaces/SubscriptionLevel";
import { useCmsLocale } from "../../../i18n/cmsLocale";

const useStyles = makeStyles({
  sectionTitle: {
    display: "block",
    marginBottom: tokens.spacingVerticalL,
  },
  card: {
    minHeight: "150px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "start",
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
    ...shorthands.borderRadius("15px"),
    backgroundColor: "#C9E3FF",
  },
  cardLocked: {
    backgroundColor: tokens.colorNeutralBackground3,
    opacity: "0.6",
  },
  buttonPriceWrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
    marginTop: "auto",
  },
  button: {
    boxShadow: tokens.shadow16Brand,
    minWidth: "140px",
    ...shorthands.borderStyle("none"),
    ...shorthands.borderRadius(tokens.spacingHorizontalXL),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalXXL),
  },
  buttonPrimary: {
    backgroundColor: tokens.colorBrandBackground,
    color: "#ffffff",
    ":hover": {
      "!:disabled": {
        color: "#dfdfdfff",
        backgroundColor: tokens.colorBrandBackgroundHover,
      },
    },
    ":active": {
      color: "#dfdfdfff !important",
      backgroundColor: `${tokens.colorBrandBackgroundPressed} !important`,
    },
    ":disabled": {
      backgroundColor: `${tokens.colorNeutralBackgroundDisabled} !important`,
      color: tokens.colorNeutralForegroundDisabled,
    },
  },
  buttonSecondary: {
    backgroundColor: "#ffffff",
    color: tokens.colorBrandBackground,
    ":hover": {
      "!:disabled": {
        color: tokens.colorBrandBackground,
        backgroundColor: tokens.colorBrandBackground2Hover,
      },
    },
    ":active": {
      color: `${tokens.colorBrandBackground} !important`,
      backgroundColor: `${tokens.colorBrandBackground2Pressed} !important`,
    },
    ":disabled": {
      backgroundColor: `${tokens.colorNeutralBackgroundDisabled} !important`,
      color: tokens.colorNeutralForegroundDisabled,
    },
  },
  categoryLabel: {
    display: "block",
    marginTop: tokens.spacingVerticalXXXL,
    marginBottom: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground2,
  },
  categorySection: {
    marginBottom: tokens.spacingVerticalXXL,
  },
  requiresBadge: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: tokens.spacingHorizontalXS,
    alignSelf: "flex-start",
    marginTop: tokens.spacingVerticalS,
    backgroundColor: "rgba(41, 82, 128, 0.1)",
    color: tokens.colorBrandBackground,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    ...shorthands.borderRadius("4px"),
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
  },
  includedBadge: {
    display: "flex",
    alignItems: "center",
    columnGap: tokens.spacingHorizontalXS,
    color: tokens.colorBrandBackground,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase500,
  },
  chipGrid: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: tokens.spacingHorizontalS,
    rowGap: tokens.spacingVerticalS,
    marginTop: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalXXL,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: tokens.spacingHorizontalXS,
    ...shorthands.borderRadius("20px"),
    ...shorthands.border("2px", "solid", "transparent"),
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalM),
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    cursor: "pointer",
    backgroundColor: "transparent",
  },
  chipIncluded: {
    backgroundColor: tokens.colorBrandBackground,
    ...shorthands.borderColor(tokens.colorBrandBackground),
    color: "#ffffff",
    cursor: "default",
  },
  chipSelected: {
    backgroundColor: "rgba(41, 82, 128, 0.12)",
    ...shorthands.borderColor(tokens.colorBrandBackground),
    color: tokens.colorBrandBackground,
  },
  chipAvailable: {
    ...shorthands.borderColor(tokens.colorNeutralStroke1),
    color: tokens.colorNeutralForeground1,
    ":hover": {
      ...shorthands.borderColor(tokens.colorBrandBackground),
      color: tokens.colorBrandBackground,
    },
  },
  kravCounter: {
    display: "block",
    marginBottom: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground2,
  },
});

type OrderAddonsProps = {
  subscriptionData: Plan[];
  showPrices?: boolean;
};

export const OrderAddons = ({
  subscriptionData,
  showPrices = false,
}: OrderAddonsProps) => {
  const classes = useStyles();
  const locale = useCmsLocale();

  const { data: addonsData } = useQuery<Pick<Query, "additionalModules">>(
    GET_ADDITIONAL_MODULES,
    { variables: { locale } },
  );
  const { data: baseModulesData } = useQuery<Pick<Query, "baseModules">>(
    GET_BASE_MODULES,
    { variables: { locale } },
  );
  const { data: processesData } = useQuery<Pick<Query, "processes">>(
    GET_PROCESSES,
    { variables: { locale } },
  );

  const additionalModules: AdditionalModule[] = useMemo(
    () =>
      (addonsData?.additionalModules ?? [])
        .filter((a): a is NonNullable<typeof a> => !!a)
        .map((a) => ({
          id: a.documentId,
          name: a.name ?? "",
          monthlyCost: a.monthlyCost ?? 0,
          description: a.description ?? undefined,
          category: a.category ?? undefined,
          requiredBaseModule: a.requiredBaseModule
            ? {
                id: a.requiredBaseModule.documentId,
                name: a.requiredBaseModule.name ?? undefined,
              }
            : null,
          subscriptions: (a.subscriptions ?? [])
            .filter((s): s is NonNullable<typeof s> => !!s)
            .map((s) => ({ id: s.documentId, title: s.title ?? undefined })),
        })),
    [addonsData]
  );

  const baseModules: BaseModule[] = useMemo(
    () => (baseModulesData?.baseModules ?? []).filter((b): b is BaseModule => !!b),
    [baseModulesData]
  );

  const allProcesses: Process[] = useMemo(
    () => (processesData?.processes ?? []).filter((p): p is Process => !!p),
    [processesData]
  );

  const { watch, setValue, getValues } = useFormContext<OrderFormData>();

  const selectedAdditionalModules = watch("additionalModules") ?? [];
  const selectedBaseModules = watch("baseModules") ?? [];
  const selectedProcesses = watch("processes") ?? [];
  const subEnum = watch("subscription");

  const selectedSubscription = useMemo(
    () =>
      subscriptionData.find(
        (s) => mapTitleToSubscriptionLevel(s.title ?? "") === subEnum
      ),
    [subscriptionData, subEnum]
  );

  const isSubscriptionStartup = subEnum === SubscriptionLevel.STARTUP;

  // Auto-include base modules linked to the selected subscription.
  // Skip for CUSTOM — the user has already made their selections.
  useEffect(() => {
    if (subEnum === SubscriptionLevel.CUSTOM) return;
    const included = baseModules
      .filter((bm) =>
        bm.subscriptions?.some(
          (sub) => sub?.documentId === selectedSubscription?.documentId
        )
      )
      .map((bm) => ({
        id: bm.documentId,
        name: bm.name ?? "",
        monthlyCost: 0,
        description: bm.description ?? undefined,
      }));
    setValue("baseModules", included, { shouldDirty: false });
  }, [baseModules, selectedSubscription, subEnum, setValue]);

  // Auto-include addons linked to the selected subscription.
  // Skip for CUSTOM — the user has already made their selections.
  useEffect(() => {
    if (subEnum === SubscriptionLevel.CUSTOM) return;
    const included = additionalModules
      .filter((addon) =>
        addon.subscriptions?.some(
          (sub) => sub.id === selectedSubscription?.documentId
        )
      )
      .map((addon) => ({ ...addon, monthlyCost: 0 }));
    setValue("additionalModules", included, { shouldDirty: false });
  }, [additionalModules, selectedSubscription, subEnum, setValue]);

  const toggleBaseModule = (bm: BaseModule) => {
    const current = getValues("baseModules") ?? [];
    const exists = current.some((m) => m.id === bm.documentId);
    const updated = exists
      ? current.filter((m) => m.id !== bm.documentId)
      : [
          ...current,
          {
            id: bm.documentId,
            name: bm.name ?? "",
            monthlyCost: bm.monthlyCost ?? 0,
            description: bm.description ?? undefined,
          },
        ];
    setValue("baseModules", updated, { shouldDirty: true });
  };

  const toggleProcess = (ko: Process) => {
    const current = getValues("processes") ?? [];
    const exists = current.some((k) => k.id === ko.documentId);
    const updated = exists
      ? current.filter((k) => k.id !== ko.documentId)
      : [
          ...current,
          {
            id: ko.documentId,
            name: ko.name ?? "",
            monthlyCost: ko.monthlyCost ?? 0,
          },
        ];
    setValue("processes", updated, { shouldDirty: true });
  };

  const toggleAdditionalModule = (addon: AdditionalModule) => {
    const current = getValues("additionalModules") ?? [];
    const exists = current.some((a) => a.name === addon.name);
    const updated = exists
      ? current.filter((a) => a.name !== addon.name)
      : [...current, addon];
    setValue("additionalModules", updated, { shouldDirty: true });
  };

  // Group addons by their required base module name (or "Övrigt" if none)
  const additionalModulesByCategory = useMemo(() => {
    const map = new Map<string, AdditionalModule[]>();
    additionalModules.forEach((addon) => {
      const cat = addon.requiredBaseModule?.name ?? "Övrigt";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(addon);
    });
    return map;
  }, [additionalModules]);

  return (
    <div>
      {/* Base Modules section */}
      <Text size={600} weight="bold" className={classes.sectionTitle}>
        {t("BaseModules")}
      </Text>
      <Grid>
        {baseModules.map((bm) => {
          const isIncluded = selectedBaseModules.some((m) => m.id === bm.documentId && m.monthlyCost === 0);
          const isSelected = selectedBaseModules.some((m) => m.id === bm.documentId);
          return (
            <GridItem size="6/12" key={bm.documentId}>
              <div className={classes.card}>
                <Text size={400} weight="bold">{bm.name}</Text>
                <Text size={300}>{bm.description}</Text>
                <div className={classes.buttonPriceWrapper}>
                  <Text size={500} weight="semibold">
                    {isIncluded ? null : showPrices ? `${formatPrice(bm.monthlyCost ?? 0)} ` : null}
                    {!isIncluded && showPrices ? <Text size={300}>{t("CurrencyPerMonth")}</Text> : null}
                  </Text>
                  {isIncluded ? (
                    <span className={classes.includedBadge}>
                      <CheckmarkCircleFilled /> {t("Included")}
                    </span>
                  ) : (
                    <Button
                      icon={isSelected ? <CheckmarkRegular /> : <AddRegular />}
                      size="large"
                      type="button"
                      onClick={() => toggleBaseModule(bm)}
                      className={mergeClasses(classes.button, isSelected ? classes.buttonSecondary : classes.buttonPrimary)}
                    >
                      {isSelected ? t("Added") : t("Add")}
                    </Button>
                  )}
                </div>
              </div>
            </GridItem>
          );
        })}
      </Grid>

      {/* Processer section */}
      {allProcesses.length > 0 && (
        <>
          <Divider appearance="strong" style={{ marginTop: tokens.spacingVerticalXXL, marginBottom: tokens.spacingVerticalXXL }} />
          <Text size={600} weight="bold" className={classes.sectionTitle}>
            {t("Processes")}
          </Text>
          <Text size={400} className={classes.kravCounter}>
            {`${selectedProcesses.length} valda · ${selectedSubscription?.includedProcesses ?? 0} ingår i paketet`}
          </Text>
          <div className={classes.chipGrid}>
            {allProcesses.map((ko) => {
              const isSelected = selectedProcesses.some((k) => k.id === ko.documentId);
              return (
                <button
                  key={ko.documentId}
                  type="button"
                  className={mergeClasses(
                    classes.chip,
                    isSelected ? classes.chipSelected : classes.chipAvailable
                  )}
                  onClick={() => toggleProcess(ko)}
                >
                  {isSelected ? <CheckmarkRegular /> : <AddRegular />}
                  {ko.name}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Modules section */}
      <Divider appearance="strong" style={{ marginTop: tokens.spacingVerticalXXL, marginBottom: tokens.spacingVerticalXXL }} />
      <Text size={600} weight="bold" className={classes.sectionTitle}>
        {t("Modules")}
      </Text>

      {Array.from(additionalModulesByCategory.entries()).map(([category, categoryAddons]) => (
        <div key={category} className={classes.categorySection}>
          <Text size={400} weight="semibold" className={classes.categoryLabel}>
            {category}
          </Text>
          <Grid>
            {categoryAddons.map((addon, index) => {
              const isSelected = selectedAdditionalModules.some((a) => a.name === addon.name);
              const isIncluded =
                addon.subscriptions?.some(
                  (sub) => sub.id === selectedSubscription?.documentId
                ) ?? false;
              const requiredBmId = addon.requiredBaseModule?.id;
              const isLocked =
                isSubscriptionStartup ||
                (!!requiredBmId && !selectedBaseModules.some((m) => m.id === requiredBmId));
              const requiredBmName = addon.requiredBaseModule?.name;

              return (
                <GridItem size="6/12" key={index}>
                  <div className={mergeClasses(classes.card, isLocked ? classes.cardLocked : undefined)}>
                    <Text size={400} weight="bold">{addon.name}</Text>
                    <Text size={300}>{addon.description}</Text>
                    <div className={classes.buttonPriceWrapper}>
                      <Text size={500} weight="semibold">
                        {isIncluded ? null : showPrices ? `${formatPrice(addon.monthlyCost ?? 0)} ` : null}
                        {!isIncluded && showPrices ? <Text size={300}>{t("CurrencyPerMonth")}</Text> : null}
                      </Text>
                      {isIncluded ? (
                        <span className={classes.includedBadge}>
                          <CheckmarkCircleFilled /> {t("Included")}
                        </span>
                      ) : isLocked ? (
                        <Tooltip
                          content={requiredBmName ? t("RequiresBaseModule", { name: requiredBmName }) : t("NotAvailable")}
                          relationship="description"
                        >
                          <span>
                            <Button
                              icon={<LockClosedRegular />}
                              size="large"
                              type="button"
                              className={mergeClasses(classes.button, classes.buttonPrimary)}
                              disabled
                              style={{ pointerEvents: "none" }}
                            >
                              {t("NotAvailable")}
                            </Button>
                          </span>
                        </Tooltip>
                      ) : (
                        <Button
                          icon={isSelected ? <CheckmarkRegular /> : <AddRegular />}
                          size="large"
                          type="button"
                          onClick={() => toggleAdditionalModule(addon)}
                          className={mergeClasses(classes.button, isSelected ? classes.buttonSecondary : classes.buttonPrimary)}
                        >
                          {isSelected ? t("Added") : t("Add")}
                        </Button>
                      )}
                    </div>
                  </div>
                </GridItem>
              );
            })}
          </Grid>
        </div>
      ))}
    </div>
  );
};
